import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * usePhotoUpload Hook
 *
 * Shared hook for photo capture and file upload functionality.
 * Used by MapObjectPopup and GalleryModal for DRY upload handling.
 *
 * Features:
 * - GPS distance checking for camera captures
 * - Structured filename generation
 * - TUS resumable upload via upload queue
 * - Upload complete event dispatching (handled by queue)
 */
import { useRef, useCallback, useState } from 'react';
import { useDialog } from '../context/DialogContext';
import { addFilesToQueue } from '../../../services/uploadStorage';
/**
 * Check GPS distance restriction for camera uploads
 */
function checkGpsDistance(structureCoords) {
    const cfnData = window.CFN?.cfnData;
    if (!cfnData?.easting || !cfnData?.northing) {
        // No GPS position available, allow upload
        return { allowed: true };
    }
    if (!structureCoords) {
        // No structure coordinates, allow upload
        return { allowed: true };
    }
    const { easting: structureEasting, northing: structureNorthing } = structureCoords;
    if (isNaN(structureEasting) || isNaN(structureNorthing)) {
        // Invalid coordinates, allow upload
        return { allowed: true };
    }
    const distance = Math.sqrt((structureEasting - cfnData.easting) ** 2 +
        (structureNorthing - cfnData.northing) ** 2);
    const maxDistance = (cfnData.position_accuracy || 0) + 20;
    if (distance > maxDistance) {
        const distanceAway = Math.round(distance - (cfnData.position_accuracy || 0));
        return {
            allowed: false,
            message: `You are at least ${distanceAway} metres away from this asset`,
        };
    }
    return { allowed: true };
}
export function usePhotoUpload({ objectType, objectId, structureCoords, onUploadComplete, }) {
    const cameraInputRef = useRef(null);
    const uploadInputRef = useRef(null);
    const [isUploading, setIsUploading] = useState(false);
    const { info, toast } = useDialog();
    /**
     * Trigger camera with GPS distance check
     */
    const triggerCamera = useCallback(async () => {
        const gpsCheck = checkGpsDistance(structureCoords);
        if (!gpsCheck.allowed) {
            await info({
                title: 'Too Far Away',
                message: gpsCheck.message || 'You are too far from this asset',
                variant: 'warning',
            });
            return;
        }
        cameraInputRef.current?.click();
    }, [structureCoords, info]);
    /**
     * Trigger file upload picker
     */
    const triggerUpload = useCallback(() => {
        uploadInputRef.current?.click();
    }, []);
    /**
     * Handle file upload with TUS resumable upload queue
     */
    const handleFiles = useCallback(async (files, _type) => {
        if (!files || files.length === 0)
            return;
        try {
            setIsUploading(true);
            // Get browser location for the upload context
            const cfnData = window.CFN?.cfnData;
            const browserLocation = cfnData?.easting ? {
                latitude: cfnData.latitude || 0,
                longitude: cfnData.longitude || 0,
                accuracy: cfnData.position_accuracy || 0,
                timestamp: Date.now(),
            } : null;
            // Add files to the TUS upload queue
            // The main app's useUploadQueue handles processing and completion
            await addFilesToQueue(Array.from(files), {
                objectType,
                objectId,
                browserLocation,
            });
            // Show brief confirmation toast
            toast({
                message: `${files.length} photo${files.length !== 1 ? 's' : ''} queued`,
                variant: 'success',
            });
            // Call completion callback
            // Note: actual upload completion is handled by the queue
            onUploadComplete?.();
        }
        catch (error) {
            console.error('[usePhotoUpload] Queue error:', error);
            await info({
                title: 'Upload Error',
                message: error instanceof Error ? error.message : 'Failed to queue files',
                variant: 'error',
            });
        }
        finally {
            setIsUploading(false);
            // Reset file inputs so the same file can be selected again
            if (cameraInputRef.current)
                cameraInputRef.current.value = '';
            if (uploadInputRef.current)
                uploadInputRef.current.value = '';
        }
    }, [objectType, objectId, info, toast, onUploadComplete]);
    /**
     * Render hidden file inputs
     */
    const renderFileInputs = useCallback(() => {
        return (_jsxs(_Fragment, { children: [_jsx("input", { ref: cameraInputRef, type: "file", accept: "image/*", capture: "environment", style: { display: 'none' }, onChange: (e) => handleFiles(e.target.files, 'camera') }), _jsx("input", { ref: uploadInputRef, type: "file", accept: "image/*, video/*, application/pdf", multiple: true, style: { display: 'none' }, onChange: (e) => handleFiles(e.target.files, 'upload') })] }));
    }, [handleFiles]);
    return {
        cameraInputRef,
        uploadInputRef,
        isUploading,
        triggerCamera,
        triggerUpload,
        handleFiles,
        renderFileInputs,
    };
}
export default usePhotoUpload;
